import { Card } from "../../../components/Card";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { useQuery } from "@tanstack/react-query";
import { api } from "../../../services/api";
import dayjs from "dayjs";
import { useApp } from "../../../services/app-context";
import { useMemo } from "react";

const CustomYAxisTick = ({
    x = 0,
    y = 0,
    payload,
}: {
    x?: number;
    y?: number;
    payload?: { value?: string };
}) => {
    return (
    <text x={x - 20} y={y} fontSize={"clamp(10px, 1.5vw, 13px)"} textAnchor="end" fill="#666">
      {payload?.value}
    </text>
  )};

export function AnalyticsCard({ title, value }: { title: string, value: number | string }) {
    return (
        <div className="px-3 py-2.5 bg-gray-301 rounded-md flex flex-col gap-2 w-full">
            <div className="opacity-60 text-black text-[clamp(10px,1.5vw,13px)] font-normal leading-snug">{title}</div>
            <div className="text-black text-[clamp(14px,1.5vw,17px)] font-semibold">{value}</div>
        </div>
    )
}

function Chart() {
    const { styles } = useApp() as any;
    const {
        data: sessionHistory,
        isLoading: isLoadingSessionHistory,
        refetch: refetchSessionHistory,
    } = useQuery({
        queryKey: ["get-session-history"],
        queryFn: () => api.sessionHistory(),
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
    });

    const history = useMemo(
      () =>
        (sessionHistory?.data || []).map((item) => ({
          day: dayjs.tz(item.day, "UTC").utc().format("ddd, MMM D"),
          sessions: item.sessions,
        })),
      [sessionHistory],
    );

    const isEmpty = useMemo(() => {
        return history.every(item => item.sessions <= 0);
    }, [history]);

    if (isEmpty) {
        return <div className="flex flex-col gap-2 text-center text-text-primary text-[15px] font-normal h-full justify-center items-center">
            <span className="font-bold">No session history for the last 7 days</span>
            <span className="opacity-60 text-sm">Start training with PraxisPlay to see your daily session progress here.</span>
        </div>;
    }

    return (
        <ResponsiveContainer width="100%" height="100%">
            <AreaChart
                width={500}
                height={400}
                data={history}
                margin={{
                    top: 10,
                    right: 40,
                    left: -18,
                    bottom: 0,
                }}
            >
                <defs>
                    <linearGradient id="colorValue" x1="0" y1="1" x2="0" y2="0">
                        <stop offset="0%" stopColor="#FFFFFF" />
                        <stop offset="40%" stopColor={styles?.colors?.primary6 || "#EFBAA1"} />
                        <stop offset="76%" stopColor={styles?.colors?.primary5 || "#DB622B"} />
                        <stop offset="100%" stopColor={styles?.colors?.primary3 || "#DB622B"} />
                    </linearGradient>
                </defs>
                <XAxis
                    dataKey="day"
                    scale="point"
                    axisLine={false}
                    tickLine={false}
                    tickMargin={10}
                    tickFormatter={(value) => (typeof value === "string" && value.includes(", ")) ? value.split(", ")[1]?.trim() ?? value : value}
                    fontSize={"clamp(10px, 1.5vw, 13px)"}
                    interval="preserveStartEnd"
                />
                <YAxis width={60} axisLine={false} tickLine={false} allowDecimals={false} tick={<CustomYAxisTick />} />
                <Tooltip />
                <Area type="monotone" dataKey="sessions" stroke={styles?.colors?.primary3 || "#B93823"} strokeWidth={2}
                    fill="url(#colorValue)" />
            </AreaChart>
        </ResponsiveContainer>
    )
}

export function SessionChart() {
    const {
        data: sessionCounts,
        isLoading: isLoadingSessionCounts,
        refetch: refetchSessionCounts,
    } = useQuery({
        queryKey: ["session-counts"],
        queryFn: () => api.sessionCounts(),
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
    });

    const {
        data: sessionDuration,
        isLoading: isLoadingSessionDuration,
        refetch: refetchSessionDuration,
    } = useQuery({
        queryKey: ["session-duration"],
        queryFn: () => api.sessionDuration(),
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
    });

    const counts = sessionCounts?.data || {};
    const duration = sessionDuration?.data || {};

    return (
        <Card className="rounded-[14px] h-full flex-col justify-start items-start gap-1.5 flex !p-[18px]">
            <div className="text-text-primary text-[clamp(14px, 1.5vw, 17px)] font-semibold">Previous Sessions</div>
            <div className="mb-1 opacity-60 text-text-primary text-[15px] font-normal leading-snug">
                Your sessions at a glance.
            </div>
            <div className="items-center gap-2 flex w-full">
                <AnalyticsCard title="This Week" value={counts?.thisWeekSessions || 0} />
                <AnalyticsCard title="Last Week" value={counts?.lastWeekSessions || 0} />
                <AnalyticsCard title="Last Month" value={counts?.lastMonthSessions || 0} />
                <AnalyticsCard title="Total Sessions" value={counts?.allSessions || 0} />
                <AnalyticsCard title="Avg. Duration" value={duration?.averageDurationHRF ?? "-"} />
            </div>
            <div className="w-full text-[clamp(11px,1.5vw,13px)] font-semibold text-text-primary mt-2">
                Daily Sessions
            </div>
            <div className="w-full h-[200px] mt-2">
                <Chart />
            </div>
        </Card>
    );
}
